import { useFeatureValue } from '@growthbook/growthbook-react';
import { useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import SwiperCore, { Autoplay } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import { INewProduct } from '../../../../interfaces/NewProduct.interfaces';
import { IOpenMarketProduct } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import { NewProductPreviewCard } from '../../../../views/Product/listView/NewProductPreviewCard/NewProductPreviewCard';
import { OpenMarketProductPreviewCard } from '../../../../views/Product/listView/OpenMarketProductPreviewCard/OpenMarketProductPreviewCard';
import { ProductPreviewCard } from '../../../../views/Product/listView/ProductPreviewCard/ProductPreviewCard';

SwiperCore.use([Autoplay]);

interface RecommendedProductsSectionProps {
  products?: IProduct[];
  openMarketProducts?: IOpenMarketProduct[];
  newProducts?: INewProduct[];
}
type RecommendedProductType = IProduct[] | IOpenMarketProduct[] | INewProduct[];

export const RecommendedProductsSection = ({
  products,
  openMarketProducts,
  newProducts,
}: RecommendedProductsSectionProps) => {
  const swiperRef = useRef<SwiperRef>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const firstSlideIndex = 0;
  const productsSlideIndex = firstSlideIndex;
  const openMarketProductsSlideIndex = products && products.length > 0 ? productsSlideIndex + 1 : firstSlideIndex;

  const abTestVariantText = useFeatureValue('text_change', '다양하게 보는 추천 매물');
  const abTestTabColor = useFeatureValue('button-color', '#505363') || '#505363';

  const convertProductsToDoubleArray = (products: RecommendedProductType): RecommendedProductType[] => {
    const chunkedProducts = [];
    for (let i = 0; i < products.length; i += 2) {
      chunkedProducts.push(products.slice(i, i + 2));
    }
    return chunkedProducts;
  };

  const moveToSlide = (slideIndex: number) => {
    if (slideIndex === activeIndex) {
      return;
    }
    const tabTitle = slideIndex === 0 ? '인증 트랙터' : slideIndex === 1 ? '직거래 트랙터' : '신품관';
    ampli.pvmClickHomeRecommendedTab({ title: tabTitle });
    ReactGA.event('pvm_click_home_recommended_tab', {
      title: tabTitle,
    });
    swiperRef?.current?.swiper?.slideTo(slideIndex);
  };

  const renderProductSlides = (productList: RecommendedProductType, isOpenMarket = false, isNew = false) => {
    if (!productList || productList.length === 0) {
      return null;
    }

    return (
      <CustomSwiperSlide>
        <ProductsBox>
          {convertProductsToDoubleArray(productList).map((group) => (
            <ItemsContainer key={`items-${group[0].id}`}>
              {group.map((product) => (
                <ItemWrapper key={product.id}>
                  {isOpenMarket ? (
                    <OpenMarketProductPreviewCard
                      product={product as IOpenMarketProduct}
                      pageType={'home'}
                      position={'top'}
                      isInquiryButtonVisible
                    />
                  ) : isNew ? (
                    <NewProductPreviewCard product={product as INewProduct} renderedIn={'recommended-new-product'} />
                  ) : (
                    <ProductPreviewCard product={product as IProduct} listType={'home-recommended'} />
                  )}
                </ItemWrapper>
              ))}
              {group.length === 1 && <ItemWrapper />}
            </ItemsContainer>
          ))}
        </ProductsBox>
      </CustomSwiperSlide>
    );
  };

  useEffect(() => {
    const swiperTitleObserver = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        if (isVisible) {
          swiperRef?.current?.swiper?.autoplay.start();
        } else {
          swiperRef?.current?.swiper?.autoplay.stop();
        }
      },
      { threshold: 0.1 },
    );

    const swiperTitleElement = titleRef.current;

    if (swiperTitleElement) {
      swiperTitleObserver.observe(swiperTitleElement);
    }

    return () => {
      if (swiperTitleElement) {
        swiperTitleObserver.unobserve(swiperTitleElement);
      }
    };
  }, []);

  return (
    <Container>
      <Title ref={titleRef}>{abTestVariantText || '다양하게 보는 추천 매물'}</Title>
      {/* TODO: TabPillRound 공통 컴포넌트 구현 필요성 확인 후 필요시 작업 */}
      <TabPillRoundContainer>
        {products && products.length > 0 && (
          <TabPillRound
            onClick={() => moveToSlide(productsSlideIndex)}
            isActive={activeIndex === productsSlideIndex}
            activeColor={abTestTabColor}
          >
            <TabPillRoundText isActive={activeIndex === productsSlideIndex}>{'인증 트랙터'}</TabPillRoundText>
          </TabPillRound>
        )}
        {openMarketProducts && openMarketProducts.length > 0 && (
          <TabPillRound
            onClick={() => moveToSlide(openMarketProductsSlideIndex)}
            isActive={activeIndex === openMarketProductsSlideIndex}
            activeColor={abTestTabColor}
          >
            <TabPillRoundText isActive={activeIndex === openMarketProductsSlideIndex}>
              {'직거래 트랙터'}
            </TabPillRoundText>
          </TabPillRound>
        )}
        {newProducts && newProducts.length > 0 && (
          <TabPillRound onClick={() => moveToSlide(2)} isActive={activeIndex === 2} activeColor={abTestTabColor}>
            <TabPillRoundText isActive={activeIndex === 2}>{'신품관'}</TabPillRoundText>
          </TabPillRound>
        )}
      </TabPillRoundContainer>

      <CustomSwiper
        ref={swiperRef}
        autoHeight
        autoplay={{
          delay: 4500,
        }}
        slidesPerView={1}
        spaceBetween={16}
        onSlideChange={(data) => {
          setActiveIndex(data.realIndex);
        }}
        edgeSwipeDetection
        loop
      >
        {products && renderProductSlides(products)}
        {openMarketProducts && renderProductSlides(openMarketProducts, true)}
        {newProducts && renderProductSlides(newProducts, false, true)}
      </CustomSwiper>
    </Container>
  );
};

const CustomSwiper = styled(Swiper)`
  width: 100%;
  height: auto;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  height: auto;

  cursor: pointer;
`;

const Container = styled.div`
  display: flex;

  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/XX-Large/xxl-bold */
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 26px */
`;

const TabPillRoundContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const TabPillRound = styled.div<{ isActive: boolean; activeColor: string }>`
  display: flex;
  max-height: 40px;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-round, 80px);
  background: ${(props) => (props.isActive ? props.activeColor : '#E4E4ED')};
  ${(props) => !props.isActive && 'opacity: 0.3;'}
`;

const TabPillRoundText = styled.span<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? '#fff' : '#505363')};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
  cursor: pointer;
`;

const ProductsBox = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: 16px;
`;

const ItemsContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`;

const ItemWrapper = styled.div`
  flex: 1;
`;
